import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Image, Flame, Diamond, Sparkles } from "lucide-react";
import { TrendingNFT } from "@/hooks/useAnalytics";
import { shortenWallet } from "@/lib/exportUtils";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";
import { cn } from "@/lib/utils";

export type NFTListCategory = "trending" | "recent" | "high-value";

interface TrendingNFTsCardProps {
  data: TrendingNFT[];
  title?: string;
  category?: NFTListCategory;
}

const categoryConfig = {
  trending: {
    icon: Flame,
    emoji: "🔥",
    defaultTitle: "Trending NFTs",
    gradient: "from-primary to-accent",
    textColor: "text-primary",
    borderColor: "border-primary/30",
    bgGradient: "from-primary/5 to-transparent",
  },
  recent: {
    icon: Sparkles,
    emoji: "✨",
    defaultTitle: "Recent NFTs",
    gradient: "from-cyan-500 to-blue-500",
    textColor: "text-cyan-400",
    borderColor: "border-cyan-500/30",
    bgGradient: "from-cyan-500/5 to-transparent",
  },
  "high-value": {
    icon: Diamond,
    emoji: "💎",
    defaultTitle: "High-Value NFTs",
    gradient: "from-purple-500 to-pink-500",
    textColor: "text-purple-400",
    borderColor: "border-purple-500/30",
    bgGradient: "from-purple-500/5 to-transparent",
  },
};

export function TrendingNFTsCard({
  data,
  title,
  category = "trending",
}: TrendingNFTsCardProps) {
  const { usdToSol } = useSolPrice();
  const config = categoryConfig[category];
  const Icon = config.icon;

  const formatPriceDisplay = (value: number) => {
    const usdValue = priceToUsd(value);
    const solValue = usdToSol(usdValue);
    return {
      primary: formatUsdPrice(usdValue),
      secondary: `≈ ${solValue.toFixed(4)} SOL`
    };
  };

  return (
    <Card className={cn("bg-gradient-to-br", config.bgGradient, config.borderColor)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <div className={cn("p-1.5 rounded-lg bg-gradient-to-br", config.gradient)}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className={config.textColor}>{config.emoji} {title || config.defaultTitle}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nessun NFT disponibile
          </div>
        ) : (
          <div className="space-y-3">
            {data.slice(0, 5).map((nft, index) => {
              const priceDisplay = formatPriceDisplay(nft.total_earned);
              return (
                <div
                  key={nft.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full font-bold text-sm bg-gradient-to-br text-white",
                    config.gradient
                  )}>
                    {index + 1}
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                    {nft.image_url ? (
                      <img
                        src={nft.image_url}
                        alt={nft.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{nft.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {nft.owner_name || shortenWallet(nft.owner_wallet)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-sm text-green-500">
                      {priceDisplay.primary}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {priceDisplay.secondary}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {nft.sales_count} sales
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
